/**
 * BaseSortEditor — edytor listy stałego sortowania (SortConfig[]).
 *
 * Używany w karcie „Filtry stałe" → zakładce „Sortowanie".
 * Każdy wiersz: picker kolumny + toggle asc/desc. Dodaj/usuń wiersz.
 * Reorder wierszy — poza scope MVP.
 */

import { useTranslation } from 'react-i18next';
import type { SortConfig } from '@saas/shared';
import { ArrowDown, ArrowUp, Minus, Plus } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';

export interface FieldOption {
  id: string;
  name: string;
}

interface BaseSortEditorProps {
  value: SortConfig[];
  onChange: (next: SortConfig[]) => void;
  fields: FieldOption[];
}

export function BaseSortEditor({ value, onChange, fields }: BaseSortEditorProps) {
  const { t } = useTranslation();

  function addRow() {
    const firstField = fields[0]?.id ?? '';
    onChange([...value, { column: firstField, direction: 'asc' }]);
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function updateRow(index: number, next: SortConfig) {
    onChange(value.map((row, i) => (i === index ? next : row)));
  }

  if (fields.length === 0) {
    return (
      <p className="py-2 text-xs text-text-muted">
        {t('admin.menuItemBaseFilters.noFields', {
          defaultValue: 'Brak pól do filtrowania dla tej kartoteki.',
        })}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="py-1 text-xs text-text-muted">
          {t('admin.menuItemBaseFilters.emptySort', {
            defaultValue: 'Brak sortowania — lista używa domyślnej kolejności.',
          })}
        </p>
      ) : (
        <div className="space-y-2">
          {value.map((row, index) => (
            <SortRow
              key={index}
              row={row}
              fields={fields}
              onChange={(next) => updateRow(index, next)}
              onRemove={() => removeRow(index)}
              t={t}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 rounded-md border border-accent-300 px-3 py-1.5 text-xs font-medium text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-950/20"
        data-testid="base-sort-add-row"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('admin.menuItemBaseFilters.addSort', { defaultValue: 'Dodaj sortowanie' })}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortRow
// ---------------------------------------------------------------------------

function SortRow({
  row,
  fields,
  onChange,
  onRemove,
  t,
}: {
  row: SortConfig;
  fields: FieldOption[];
  onChange: (next: SortConfig) => void;
  onRemove: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <div className="flex items-center gap-2" data-testid="base-sort-row">
      {/* Picker kolumny */}
      <Dropdown
        value={row.column}
        onChange={(v) => onChange({ ...row, column: v })}
        className="min-w-[8rem] flex-1 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-xs text-text-primary focus:ring-1 focus:ring-accent-500"
        data-testid="base-sort-column"
        options={fields.map((f) => ({ value: f.id, label: f.name }))}
      />

      {/* Toggle asc / desc */}
      <button
        type="button"
        onClick={() => onChange({ ...row, direction: row.direction === 'asc' ? 'desc' : 'asc' })}
        className="flex shrink-0 items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-xs text-text-secondary hover:bg-bg-secondary"
        data-testid="base-sort-direction"
        title={
          row.direction === 'asc'
            ? t('admin.menuItemBaseFilters.sortAsc', { defaultValue: 'Rosnąco' })
            : t('admin.menuItemBaseFilters.sortDesc', { defaultValue: 'Malejąco' })
        }
      >
        {row.direction === 'asc' ? (
          <>
            <ArrowUp className="h-3.5 w-3.5" />
            {t('admin.menuItemBaseFilters.sortAsc', { defaultValue: 'Rosnąco' })}
          </>
        ) : (
          <>
            <ArrowDown className="h-3.5 w-3.5" />
            {t('admin.menuItemBaseFilters.sortDesc', { defaultValue: 'Malejąco' })}
          </>
        )}
      </button>

      {/* Usuń wiersz */}
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 rounded-md border border-border-secondary p-1.5 text-text-muted hover:border-red-300 hover:text-red-600"
        title={t('common.delete', { defaultValue: 'Usuń' })}
        data-testid="base-sort-remove"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
